import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

type MermaidVisibilityByEntryId = Record<string, boolean>;

export interface DocumentMermaidVisibilityContextValue {
  isSourceVisible: (entryId: string) => boolean;
  setSourceVisible: (entryId: string, visible: boolean) => void;
}

const DocumentMermaidVisibilityContext = createContext<
  DocumentMermaidVisibilityContextValue | undefined
>(undefined);

function getStorageKey(documentPath: string): string {
  return `document-${documentPath}-mermaid`;
}

function readVisibilityMap(documentPath: string): MermaidVisibilityByEntryId {
  if (typeof window === 'undefined') return {};

  const raw = window.localStorage.getItem(getStorageKey(documentPath));
  if (!raw) return {};

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};

    const entries = Object.entries(parsed as Record<string, unknown>);
    return entries.reduce<MermaidVisibilityByEntryId>(
      (acc, [entryId, value]) => {
        if (typeof value === 'boolean') {
          acc[entryId] = value;
        }

        return acc;
      },
      {},
    );
  } catch {
    return {};
  }
}

export function DocumentMermaidVisibilityProvider({
  documentPath,
  children,
}: {
  documentPath: string;
  children: ReactNode;
}) {
  const [visibilityByEntryId, setVisibilityByEntryId] =
    useState<MermaidVisibilityByEntryId>({});

  useEffect(() => {
    setVisibilityByEntryId(readVisibilityMap(documentPath));
  }, [documentPath]);

  const isSourceVisible = useCallback(
    (entryId: string) => Boolean(visibilityByEntryId[entryId]),
    [visibilityByEntryId],
  );

  const setSourceVisible = useCallback(
    (entryId: string, visible: boolean) => {
      setVisibilityByEntryId((prev) => {
        const next = { ...prev, [entryId]: visible };
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(
            getStorageKey(documentPath),
            JSON.stringify(next),
          );
        }

        return next;
      });
    },
    [documentPath],
  );

  const value = useMemo<DocumentMermaidVisibilityContextValue>(
    () => ({
      isSourceVisible,
      setSourceVisible,
    }),
    [isSourceVisible, setSourceVisible],
  );

  return (
    <DocumentMermaidVisibilityContext.Provider value={value}>
      {children}
    </DocumentMermaidVisibilityContext.Provider>
  );
}

export function useDocumentMermaidVisibility() {
  const context = useContext(DocumentMermaidVisibilityContext);
  if (!context) {
    throw new Error(
      'useDocumentMermaidVisibility must be used within DocumentMermaidVisibilityProvider',
    );
  }

  return context;
}
